import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar, detectModuleContext } from "@/components/layout/Sidebar";
import { Footer } from "@/components/layout/Footer";
import { CommandPalette } from "@/components/search/CommandPalette";
import { AiAssistantPanel } from "@/components/ai/AiAssistantPanel";
import { AiAssistantFab } from "@/components/ai/AiAssistantFab";
import { useAiAssistant } from "@/modules/marketing/ai/useAiAssistant";
import { useSidebarCollapsed } from "@/hooks/useSidebarCollapsed";

/**
 * AppShell — Anthropic redesign layout shell (Sprint R2).
 *
 * Tuzilish (TopBar OLIB TASHLANDI — faqat breadcrumb ko'rsatardi, funksional
 * element yo'q edi; qidiruv/user/tenant switcher allaqachon Sidebar'da):
 *   ┌──────────────────────────────────────────┐
 *   │ Sidebar (272↔64px) │ main content        │
 *   │                 ├────────────────────────┤
 *   │ workspace       │ px-8 py-8 (2xl:px-10)  │
 *   │ groups          │ max-w 1280px (home)    │
 *   │ pinned          │                        │
 *   │ user/lang/cog   ├────────────────────────┤
 *   │                 │ Footer (40px)          │
 *   └──────────────────────────────────────────┘
 *
 * Global shortcut'lar:
 *   - Cmd/Ctrl + K — Command Palette (qidiruv — sidebardan emas, global)
 *   - Cmd/Ctrl + J — AI panel (useAiAssistant store toggle)
 *   - Cmd/Ctrl + B — Sidebar yig'ish/ochish
 *
 * AI panel endi `useAiAssistant` store orqali boshqariladi (AiAssistantPanel
 * backendga ulangan). O'ng pastda ko'rinadigan AiAssistantFab tugmasi bor.
 */
export function AppShell() {
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Sidebar holati shell darajasida — flex layout sabab aside kengligi
  // o'zgarsa main avtomatik suriladi (qo'shimcha margin shart emas).
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();

  const aiOpen = useAiAssistant((s) => s.open);
  const openAiChat = useAiAssistant((s) => s.openChat);
  const closeAi = useAiAssistant((s) => s.close);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isCmdK =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !e.shiftKey;
      const isCmdJ =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j" && !e.shiftKey;
      const isCmdB =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b" && !e.shiftKey;

      if (isCmdB) {
        e.preventDefault();
        toggleSidebar();
        return;
      }

      if (isCmdJ) {
        e.preventDefault();
        // Store holatiga qarab toggle — ochiq bo'lsa yop, yopiq bo'lsa och.
        if (useAiAssistant.getState().open) {
          closeAi();
        } else {
          openAiChat();
        }
        return;
      }

      if (!isCmdK) return;

      // Search sahifalarida sahifa o'z handler'ini ishlatadi (input fokus)
      if (
        location.pathname.startsWith("/sales/search") ||
        location.pathname.startsWith("/sales/knowledge/search")
      ) {
        return;
      }

      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [location.pathname, toggleSidebar, openAiChat, closeAi]);

  // CommandPalette / AiChatBar → AI panel ni ochishi uchun custom event tinglovchi.
  // AiChatBar `ai-panel:open` detail.prompt bilan emit qiladi — generic chat ochiladi.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ prompt?: string }>).detail;
      const prompt = detail?.prompt?.trim();
      openAiChat(
        prompt ? { page: "generic", autoPrompt: prompt } : undefined,
      );
    };
    window.addEventListener("ai-panel:open", onOpen);
    return () => window.removeEventListener("ai-panel:open", onOpen);
  }, [openAiChat]);

  // Route o'zgarganda palette yopilsin
  useEffect(() => {
    setPaletteOpen(false);
  }, [location.pathname]);

  const moduleContext = useMemo(
    () => detectModuleContext(location.pathname),
    [location.pathname],
  );
  const isHome = location.pathname === "/";
  const showSidebar = Boolean(moduleContext) && !isHome;

  return (
    <div className="min-h-screen flex bg-[var(--bg-base)] text-[var(--text-primary)]">
      {/* Sidebar faqat modul kontekstida ko'rinadi (bosh sahifada YO'Q) */}
      {showSidebar && moduleContext && (
        <Sidebar
          context={moduleContext}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleSidebar}
        />
      )}
      <div className="flex flex-1 min-w-0 flex-col">
        <main className="flex flex-1 flex-col">
          <div className="flex-1 px-8 py-8 2xl:px-10">
            <div
              className="mx-auto w-full"
              style={{ maxWidth: isHome ? 1280 : undefined }}
            >
              <Outlet />
            </div>
          </div>
        </main>
        <Footer />
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      {/* Context-aware AI panel — useAiAssistant store + backend bilan ulangan */}
      <AiAssistantPanel />
      {/* Ko'rinadigan ochish tugmasi — panel yopiq paytda o'ng pastda */}
      {!aiOpen && <AiAssistantFab />}
    </div>
  );
}
